/**
 * Helper utilities for cleaning up RTC media connections and tracks.
 * These utilities ensure complete disconnection and resource cleanup.
 */

/**
 * Aggressively stop all media tracks from senders and receivers
 */
export function stopAllMediaTracks(pc: RTCPeerConnection): void {
  // Stop all outgoing tracks (unless marked as centrally managed)
  if (typeof pc.getSenders === 'function') {
    pc.getSenders().forEach((sender: RTCRtpSender) => {
      if (
        sender.track &&
        !sender.track.mercuryManaged &&
        typeof sender.track.stop === 'function'
      ) {
        try {
          sender.track.stop();
        } catch (_e) {}
      }

      // Always remove the track from the sender if possible
      if (typeof sender.replaceTrack === 'function') {
        sender.replaceTrack(null).catch(() => {});
      }
    });
  }

  // Stop all incoming tracks
  if (typeof pc.getReceivers === 'function') {
    pc.getReceivers().forEach((receiver: RTCRtpReceiver) => {
      if (receiver.track && typeof receiver.track.stop === 'function') {
        try {
          receiver.track.stop();
        } catch (_e) {}
      }
    });
  }

  // Stop tracks from transceivers as well
  if (typeof pc.getTransceivers === 'function') {
    pc.getTransceivers().forEach((transceiver: RTCRtpTransceiver) => {
      try {
        // Stop transceiver itself (ends negotiation, not the same as stopping tracks)
        if (typeof transceiver.stop === 'function') {
          transceiver.stop();
        }
        // Stop sender track — but respect mercuryManaged, same as the sender loop above
        if (
          transceiver.sender?.track &&
          !transceiver.sender.track.mercuryManaged &&
          typeof transceiver.sender.track.stop === 'function'
        ) {
          transceiver.sender.track.stop();
        }
        // Stop receiver track (remote tracks are never mercury-managed)
        if (
          transceiver.receiver?.track &&
          typeof transceiver.receiver.track.stop === 'function'
        ) {
          transceiver.receiver.track.stop();
        }
      } catch (_e) {}
    });
  }
}

/**
 * Clear any MediaStreams that might still be referenced
 */
export function clearMediaStreams(pc: RTCPeerConnection): void {
  try {
    // If there are any local streams still attached, remove them
    // Note: getLocalStreams/getRemoteStreams are deprecated but may still exist
    const pcAny = pc as any;
    if (typeof pcAny.getLocalStreams === 'function') {
      pcAny.getLocalStreams().forEach((stream: MediaStream) => {
        stream.getTracks().forEach((track: MediaStreamTrack) => {
          try {
            if (!track.mercuryManaged) {
              track.stop();
            }
            stream.removeTrack(track);
          } catch (_e) {}
        });
      });
    }

    // Same for remote streams
    if (typeof pcAny.getRemoteStreams === 'function') {
      pcAny.getRemoteStreams().forEach((stream: MediaStream) => {
        stream.getTracks().forEach((track: MediaStreamTrack) => {
          try {
            track.stop();
          } catch (_e) {}
        });
      });
    }
  } catch (_e) {
    // These methods might not exist in all browsers/versions
  }
}
